import {
  ActionListDataItem,
  BannerWrapper,
  Dialog,
  DialogPosition,
  StackedList,
  Typography,
} from '@ecdlink/ui';
import { useHistory, useLocation } from 'react-router';
import { EditRegistersRouteState } from './edit-registers.types';
import { shallowEqual, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { classroomsSelectors } from '@/store/classroom';
import { AttendanceDto } from '@ecdlink/core';
import { useCallback, useEffect, useState } from 'react';
import { EditRegistersAttendanceListProps } from './attendance-list/attendance-list.types';
import { attendanceSelectors } from '@/store/attendance';
import ROUTES from '@/routes/routes';
import { MonthlyAttendanceReportRouteState } from '../components/attendance-report/attendance-report.types';
import EditRegistersAttendanceList from './attendance-list/attendance-list';

import { staticDataSelectors } from '@/store/static-data';
import { getMissedAttendanceSummaryGroupsForEditRegisters } from '@utils/classroom/attendance/track-attendance-utils';
import { Holiday } from '@ecdlink/graphql';

export const EditRegisters = () => {
  const [classroomName, setClassroomName] = useState<string>('');
  const [selectedRegister, setSelectedRegister] =
    useState<EditRegistersAttendanceListProps['selectedRegister']>();
  const [attendanceActionList, setAttendanceActionList] = useState<
    ActionListDataItem[]
  >([]);

  const [missedActionList, setMissedActionList] = useState<
    ActionListDataItem[]
  >([]);

  const [currentEditClassroomGroupId, setCurrentEditClassroomGroupId] =
    useState<string>();
  const [editAttendanceRegisterVisible, setEditAttendanceRegisterVisible] =
    useState<boolean>(false);
  const [selectedAttendanceRegisters, setSelectedAttendanceRegisters] =
    useState<AttendanceDto[]>([]);
  const [attendanceEditDay, setAttendanceEditDay] = useState<Date>();
  const [submitText, setSubmitText] = useState<string>(
    'Submit & go to next day'
  );
  const isValidAttendanceDay = true;
  const location = useLocation<EditRegistersRouteState>();

  const startDate = location.state.startDate;
  const endDate = location.state.endDate;
  const classroomGroups = useSelector(classroomsSelectors.getClassroomGroups);

  const publicHolidays = useSelector(staticDataSelectors.getHolidays);
  const attendanceData = useSelector(attendanceSelectors.getAttendance);
  const classroomGroupLearners = useSelector(
    classroomsSelectors.getClassroomGroupLearnersForAttendance
  );

  const monthlyReport = useSelector(
    attendanceSelectors.getClassroomAttendanceOverviewReportByPeriod2(
      location.state.startDate,
      location.state.endDate
    ),
    shallowEqual
  );

  function removeDuplicates(data: AttendanceDto[]): AttendanceDto[] {
    const seen = new Set<string>();

    return data.filter((item) => {
      const key = `${item.classroomProgrammeId}|${item.attendanceDate}`;
      if (seen.has(key)) {
        return false; // duplicate → skip
      }
      seen.add(key);
      return true; // first occurrence → keep
    });
  }

  const uniqueAttendanceRegisters = removeDuplicates(monthlyReport);

  const programmes = useSelector(classroomsSelectors.getClassProgrammes);

  const monthName = format(startDate ?? new Date(), 'MMM');

  const history = useHistory();

  const attendanceGroup = useCallback(
    (classroomGroupId) => {
      return classroomGroups?.find((item) => item.id === classroomGroupId)
        ?.name;
    },
    [classroomGroups]
  );

  const openEditRegister = (
    classroomGroupCacheId: string,
    attendanceDay: Date,
    isLast: boolean,
    classGroupName: string
  ) => {
    if (isValidAttendanceDay) {
      setClassroomName(classGroupName);

      setCurrentEditClassroomGroupId(classroomGroupCacheId);
      setAttendanceEditDay(attendanceDay);
      setEditAttendanceRegisterVisible(true);
    }

    setSubmitText(isLast ? 'Submit' : 'Submit & go to next day');
  };

  useEffect(() => {
    if (uniqueAttendanceRegisters && uniqueAttendanceRegisters.length > 0) {
      const actionListToDisplay: ActionListDataItem[] = [];

      const sortedCompletedAttendanceGroups = uniqueAttendanceRegisters?.sort(
        (a, b) => ((a.attendanceDate ?? 0) > (b.attendanceDate ?? 0) ? 1 : -1)
      );

      sortedCompletedAttendanceGroups.forEach((attendance, idx) => {
        const classroomProgramme = programmes?.find(
          (item) => item.id === attendance?.classroomProgrammeId
        );
        actionListToDisplay.push({
          title: attendanceGroup(classroomProgramme?.classroomGroupId) || '',
          subTitle: format(
            new Date(attendance.attendanceDate!),
            'EEEE, d LLLL'
          ),
          actionName: 'Edit',
          actionIcon: 'PencilIcon',
          switchTextStyles: true,
          onActionClick: () => {
            const attendanceRegisters = monthlyReport?.filter(
              (item) =>
                new Date(item.attendanceDate!).getDate() ===
                new Date(attendance.attendanceDate!).getDate()
            );
            setCurrentEditClassroomGroupId(attendance.classroomProgrammeId);
            openEditRegister(
              classroomProgramme?.classroomGroupId ?? '',
              new Date(attendance.attendanceDate!),
              idx === sortedCompletedAttendanceGroups.length - 1,
              attendanceGroup(classroomProgramme?.classroomGroupId) || ''
            );

            setSelectedAttendanceRegisters(attendanceRegisters || []);
          },
        });
      });

      const uniqueList = actionListToDisplay?.filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (t) => t.title === item.title && t.subTitle === item.subTitle
          )
      );

      setAttendanceActionList(uniqueList);
    }
  }, [monthlyReport, programmes, attendanceGroup]);

  useEffect(() => {
    if (
      classroomGroups &&
      programmes &&
      attendanceData &&
      publicHolidays &&
      startDate &&
      endDate
    ) {
      const allMissedForEdit = getMissedAttendanceSummaryGroupsForEditRegisters(
        classroomGroups,
        programmes,
        attendanceData as AttendanceDto[],
        publicHolidays as Holiday[],
        new Date(), // Up to today
        classroomGroupLearners
      );

      const missedForThisPeriod = allMissedForEdit.filter(
        (item) => item.missedDay >= startDate && item.missedDay <= endDate
      );

      if (missedForThisPeriod.length > 0) {
        const sortedMissed = missedForThisPeriod.sort((a, b) =>
          a.missedDay > b.missedDay ? 1 : -1
        );

        const missedActions: ActionListDataItem[] = sortedMissed.map(
          (group, idx) => ({
            title: group.classroomGroup.name || '',
            subTitle: format(group.missedDay, 'EEEE, d LLLL'),
            actionName: 'Complete',
            actionIcon: 'PencilIcon',
            switchTextStyles: true,
            onActionClick: () => {
              setCurrentEditClassroomGroupId(group.classroomGroup.id);
              openEditRegister(
                group.classroomGroup.id ?? '',
                group.missedDay,
                idx === sortedMissed.length - 1,
                group.classroomGroup.name || ''
              );
              setSelectedAttendanceRegisters([]);
            },
          })
        );

        setMissedActionList(missedActions);
      } else {
        setMissedActionList([]);
      }
    }
  }, [
    classroomGroups,
    programmes,
    attendanceData,
    publicHolidays,
    classroomGroupLearners,
    startDate,
    endDate,
  ]);

  return (
    <BannerWrapper
      title="Edit registers"
      subTitle={`${monthName} attendance registers`}
      size="small"
      className="p-4 pt-6"
      onBack={() =>
        history.push(ROUTES.CLASSROOM.ATTENDANCE.MONTHLY_REPORT, {
          selectedMonth: location.state.selectedMonth,
        } as MonthlyAttendanceReportRouteState)
      }
    >
      <Typography
        type="h2"
        text={`${monthName} attendance registers`}
        className="mb-8"
        color="textDark"
      />

      <StackedList
        listItems={attendanceActionList}
        type={'ActionList'}
      ></StackedList>

      {missedActionList.length > 0 && (
        <div className="mt-8">
          <Typography
            type="h2"
            text="Incomplete registers"
            className="mb-4"
            color="alertMain"
          />
          <StackedList
            listItems={missedActionList}
            type={'ActionList'}
          ></StackedList>
        </div>
      )}

      {attendanceEditDay && (
        <Dialog
          fullScreen
          visible={editAttendanceRegisterVisible}
          position={DialogPosition.Full}
        >
          <EditRegistersAttendanceList
            onBack={() => setEditAttendanceRegisterVisible(false)}
            selectedRegister={selectedRegister}
            attendanceDate={attendanceEditDay}
            classroomgroupId={currentEditClassroomGroupId}
            editAttendanceRegisterVisible={editAttendanceRegisterVisible}
            submitText={submitText}
            classroomName={classroomName}
            selectedAttendanceRegisters={selectedAttendanceRegisters}
            setEditAttendanceRegisterVisible={setEditAttendanceRegisterVisible}
          />
        </Dialog>
      )}
    </BannerWrapper>
  );
};
